/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import clsx from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';
import moment from 'moment';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import DropdownControl from '../controls/DropdownControl';
import { useData } from '../context/data.context';
import {
  CUSTOM_THRESHOLD_CONFIG,
  TOAST_MESSAGES,
  defaultThreshold
} from '../constants/global-constants';
import { updateClientThresholdSettings } from '../services/dashboard-service';
import {
  ClientThresholdSettingsAttribute,
  DeviceType,
  ParametersType,
  ThresholdConfig,
  ThresholdValues
} from '../constants/telosair-interfaces';
import ThresholdEditor from './thresholdEditior';
import CustomInputBox from '../controls/CustomInputBox';
import Loader from './loader';

type FORMDATA = {
  newConfigName: string;
};

/**
 * Threshold page component.
 * @returns - A component for threshold.
 */
export default function UserThresholdSettings(): ReactElement {
  const {
    currentUser,
    clientThresholdSettings,
    fetchClientThresholdSettings,
    loading: isDataLoading,
    setClientThresholdSettings,
    parameters
  } = useData();
  const [selectedConfig, setSelectedConfig] = useState<ThresholdConfig | null>(null);
  const [configs, setConfigs] = useState<ThresholdConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [uiLoading, setUiLoading] = useState(true);
  const [selectedDeviceTypeConfig, setSelectedDeviceTypeConfig] = useState<{
    indoor: ThresholdConfig | undefined;
    outdoor: ThresholdConfig | undefined;
    mixed: ThresholdConfig | undefined;
  }>({
    indoor: undefined,
    outdoor: undefined,
    mixed: undefined
  });
  const methods = useForm<FORMDATA>({
    mode: 'all'
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (clientThresholdSettings && clientThresholdSettings?.userCreatedConfigs) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      setConfigs([defaultThreshold, ...clientThresholdSettings?.userCreatedConfigs]);
    } else {
      setConfigs([defaultThreshold]);
    }
  }, [clientThresholdSettings]);

  /**
   * Initialize selected config for editing view once data is ready.
   * Do not override if the user has already selected/created a config.
   */
  useEffect((): void => {
    if (!isDataLoading) {
      if (!selectedConfig) {
        const initialId = clientThresholdSettings?.mixedConfigId || 'ashrae';
        const initial = configs.find((c) => c.id === initialId) || configs[0] || null;
        setSelectedConfig(initial);
      }
      setUiLoading(false);
    }
  }, [configs, clientThresholdSettings, isDataLoading, selectedConfig]);

  /**
   * Keep local per-type state in sync for instant UI feedback without waiting for API response.
   */
  useEffect((): void => {
    if (!clientThresholdSettings || configs.length === 0) return;
    /**
     * Find config by ID from available configs.
     * @param id - Config ID.
     * @returns ThresholdConfig or undefined.
     */
    const findById = (id?: string): ThresholdConfig | undefined =>
      configs.find((c) => c.id === id) || undefined;
    setSelectedDeviceTypeConfig({
      indoor: findById(clientThresholdSettings.indoorConfigId),
      outdoor: findById(clientThresholdSettings.outdoorConfigId),
      mixed: findById(clientThresholdSettings.mixedConfigId)
    });
  }, [clientThresholdSettings, configs]);

  /**
   * Function to apply new threshold settings.
   * @param configList - List of configs.
   * @param newActiveConfig - Active config.
   */
  const onSave = async (
    configList: ThresholdConfig[],
    newActiveConfig?: ThresholdConfig
  ): Promise<void> => {
    try {
      if (clientThresholdSettings) {
        const userCreatedConfigs = configList.filter((config) => {
          if (config.id === 'ashrae') {
            return false;
          }
          if (config.id === 'iecc') {
            return false;
          }
          return true;
        });
        const newConfig: ClientThresholdSettingsAttribute = {
          ...clientThresholdSettings,
          userCreatedConfigs
        };
        // active config update is handled by setConfigForType
        setLoading(true);
        await updateClientThresholdSettings(newConfig);
        await fetchClientThresholdSettings();
        setLoading(false);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Add a missing parameter to the selected configuration using default custom thresholds.
   * @param param - Parameter to add.
   */
  const handleAddParameter = async (param: ParametersType): Promise<void> => {
    if (!selectedConfig) return;
    try {
      const defaults = CUSTOM_THRESHOLD_CONFIG[param as keyof typeof CUSTOM_THRESHOLD_CONFIG];
      const updatedConfig: ThresholdConfig = {
        ...selectedConfig,
        thresholds: {
          [param]: { ...defaults },
          ...selectedConfig.thresholds
        },
        updated: new Date()
      };
      setSelectedConfig(updatedConfig);
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Apply selected config for a specific device type (Indoor/Outdoor/Mixed).
   * @param type - Device type to apply for.
   * @param config - Config to set active for the device type.
   */
  const setConfigForType = async (type: DeviceType, config: ThresholdConfig): Promise<void> => {
    if (!clientThresholdSettings) return;
    try {
      const payload: ClientThresholdSettingsAttribute = {
        ...clientThresholdSettings,
        userCreatedConfigs: clientThresholdSettings.userCreatedConfigs,
        indoorConfigId:
          type === DeviceType.INDOOR ? config.id : clientThresholdSettings.indoorConfigId,
        outdoorConfigId:
          type === DeviceType.OUTDOOR ? config.id : clientThresholdSettings.outdoorConfigId,
        mixedConfigId: type === DeviceType.MIXED ? config.id : clientThresholdSettings.mixedConfigId
      };
      // Optimistic UI updates
      setSelectedConfig(config);
      setClientThresholdSettings(payload);
      setSelectedDeviceTypeConfig((prev) => ({
        indoor: type === DeviceType.INDOOR ? config : prev.indoor,
        outdoor: type === DeviceType.OUTDOOR ? config : prev.outdoor,
        mixed: type === DeviceType.MIXED ? config : prev.mixed
      }));
      setLoading(true);
      await updateClientThresholdSettings(payload);
      await fetchClientThresholdSettings();
      setLoading(false);
      toast.success('Applied successfully');
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to handle customise button click.
   * @param config - Selected config.
   */
  const onClickCustomise = (config: ThresholdConfig): void => {
    const newConfig: ThresholdConfig = {
      ...config,
      name: '',
      description: '',
      createdBy: currentUser?.firstName || '',
      id: new Date().getTime().toString(),
      created: new Date(),
      updated: new Date(),
      inputVisible: true,
      isNew: true,
      mode: 'copy'
    };
    setConfigs((prev) => [...prev, newConfig]);
    // Defer selection slightly to avoid race with init effect
    setTimeout(() => setSelectedConfig(newConfig), 0);
  };

  /**
   * Function to handle cancel customise button click.
   * @param config - Selected config.
   */
  const onCancelCustomise = (config: ThresholdConfig): void => {
    if (config.isNew) {
      const updated = configs.filter((item) => item.id !== config.id);
      setConfigs(updated);
      const fallbackId =
        clientThresholdSettings?.mixedConfigId ||
        clientThresholdSettings?.indoorConfigId ||
        clientThresholdSettings?.outdoorConfigId ||
        'ashrae';
      const fallback = updated.find((c) => c.id === fallbackId) || updated[0] || null;
      setSelectedConfig(fallback);
    } else {
      setConfigs((prev) => [
        ...prev.filter((item) => item.id !== config.id),
        { ...config, inputVisible: false }
      ]);
    }
  };

  /**
   * Function to apply new threshold settings.
   * @param data - Formdata.
   * @param config - Config to be saved.
   */
  const onSaveNewConfig = async (data: any, config: ThresholdConfig): Promise<void> => {
    if (config) {
      const updatedConfig = { ...config, name: data[config.id], inputVisible: false, isNew: true };
      const updatedConfigs = [...configs.filter((item) => item.id !== config.id), updatedConfig];
      setSelectedConfig(updatedConfig);
      setConfigs(updatedConfigs);
      await onSave(updatedConfigs);
      toast.success('Saved successfully');
    }
  };

  /**
   * Function to apply new threshold settings.
   */
  const onClickEdit = async (): Promise<void> => {
    if (selectedConfig) {
      setConfigs((prev) => [
        ...prev.filter((item) => item.id !== selectedConfig.id),
        { ...selectedConfig, inputVisible: true }
      ]);
      reset({
        [selectedConfig.id]: selectedConfig.name
      });
    }
  };

  /**
   * Function to apply new threshold settings.
   * @param config - Config to be deleted.
   */
  const onClickDelete = async (config: ThresholdConfig): Promise<void> => {
    const updatedConfigs = [...configs.filter((item) => item.id !== config.id)];
    setConfigs(updatedConfigs);
    if (selectedConfig?.id === config.id) {
      setSelectedConfig(updatedConfigs[0] || null);
    }
    await onSave(updatedConfigs);
    toast.success('Deleted successfully');
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const onAddCustomStandard = (): void => {
    const newConfig: ThresholdConfig = {
      name: '',
      description: '',
      mode: 'custom',
      createdBy: currentUser?.firstName || '',
      id: new Date().getTime().toString(),
      created: new Date(),
      updated: new Date(),
      inputVisible: true,
      isNew: true,
      thresholds: CUSTOM_THRESHOLD_CONFIG
    };
    setConfigs((prev) => [...prev, newConfig]);
    // Defer selection slightly to avoid race with init effect
    setTimeout(() => setSelectedConfig(newConfig), 0);
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const onSaveAThreshold = async (
    values: ThresholdValues,
    parameter: ParametersType
  ): Promise<void> => {
    const config = selectedConfig;
    if (config) {
      config.thresholds[parameter] = {
        ...values,
        incomplete: false
      };
      setSelectedConfig({ ...config });
      await onSave([...configs.filter((item) => item.id !== config.id), config]);
      toast.success('Updated successfully');
    }
  };

  const handleThresholdDelete = async (parameter: ParametersType): Promise<void> => {
    try {
      const config = selectedConfig;
      if (config) {
        delete config.thresholds[parameter];
        const updatedConfigs = [...configs.filter((item) => item.id !== config.id), config];
        setConfigs(updatedConfigs);
        await onSave(updatedConfigs);
        setSelectedConfig({ ...config });
        toast.success('Deleted successfully');
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const getButtonByCase = ({
    inputVisible,
    isSelected,
    isAutoCreated,
    item
  }: {
    inputVisible: boolean;
    isSelected: boolean;
    isAutoCreated: boolean;
    item: ThresholdConfig;
  }): ReactElement => {
    if (inputVisible) {
      return (
        <div className="flex flex-col gap-4">
          <Button form="config-form" buttonType="submit" theme="primary" size="sm" name="Save" />
          <Button
            onClick={() => onCancelCustomise(item)}
            theme="secondary-color"
            size="sm"
            name="Cancel"
          />
        </div>
      );
    }
    if (!inputVisible && isSelected && !isAutoCreated) {
      return (
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <Button
              onClick={() => onClickEdit()}
              theme="secondary-color"
              size="xs"
              isBlockButton
              name="Edit name"
            />
            <Button
              onClick={() => onClickDelete(item)}
              theme="danger"
              size="xs"
              isBlockButton
              name="Delete"
            />
          </div>
        </div>
      );
    }
    if (isSelected && isAutoCreated) {
      return (
        <div className="flex flex-col gap-4">
          <Button
            onClick={() => onClickCustomise(item)}
            theme="secondary-color"
            size="sm"
            name="Customise"
          />
        </div>
      );
    }
    return <div />;
  };

  if (uiLoading || loading) {
    return <Loader />;
  }
  return (
    <div className="flex flex-1 py-4 flex-col">
      <div className="bg-container-800 p-6 rounded flex w-full gap-3 flex-1">
        {configs.map((item) => (
          <div
            className={clsx(
              'bg-background-700 p-6 min-w-[13.75rem] min-h-[7.25rem] border rounded transition-colors',
              selectedConfig?.id === item.id &&
                'border-primary-500 ring-2 ring-primary-500 bg-background-600'
            )}
            key={item.id}
          >
            <div className="flex justify-between gap-5">
              <div>
                {!item.inputVisible ? (
                  <label
                    className="text-xl font-medium text-white cursor-pointer"
                    htmlFor={item.id}
                  >
                    <input
                      checked={item.id === selectedConfig?.id}
                      onChange={() => setSelectedConfig(item)}
                      className="h-4 w-4 mr-2 cursor-pointer"
                      id={item.id}
                      type="radio"
                    />
                    <span>{item.name}</span>
                  </label>
                ) : (
                  <>
                    {/* eslint-disable-next-line */}
                    <FormProvider {...methods}>
                      <form
                        id="config-form"
                        onSubmit={handleSubmit((data) => onSaveNewConfig(data, item))}
                      >
                        <CustomInputBox
                          validationObject={{ required: 'Please enter the config name' }}
                          placeholder="Name"
                          noMarginTop
                          autoFocus
                          idname={item.id}
                        />
                      </form>
                    </FormProvider>
                  </>
                )}
                <div className="text-gray-500 text-sm font-medium mt-2">
                  {item.createdBy === 'auto' ? (
                    item.description
                  ) : (
                    <div>
                      <div>By {item.createdBy}</div>
                      <div>Modified {moment(item.updated).format('Do MMM YY')}</div>
                    </div>
                  )}
                </div>
              </div>
              {getButtonByCase({
                isSelected: selectedConfig?.id === item.id,
                inputVisible: !!item.inputVisible,
                isAutoCreated: item.createdBy === 'auto',
                item
              })}
            </div>
            {!item.inputVisible && (
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-gray-400 text-sm">Use this standard for:</span>
                <Button
                  onClick={() => setConfigForType(DeviceType.INDOOR, item)}
                  theme={
                    selectedDeviceTypeConfig.indoor?.id === item.id ? 'primary' : 'secondary-gray'
                  }
                  size="xs"
                  name="Indoor"
                />
                <Button
                  onClick={() => setConfigForType(DeviceType.OUTDOOR, item)}
                  theme={
                    selectedDeviceTypeConfig.outdoor?.id === item.id ? 'primary' : 'secondary-gray'
                  }
                  size="xs"
                  name="Outdoor"
                />
                <Button
                  onClick={() => setConfigForType(DeviceType.MIXED, item)}
                  theme={
                    selectedDeviceTypeConfig.mixed?.id === item.id ? 'primary' : 'secondary-gray'
                  }
                  size="xs"
                  name="Mixed"
                />
              </div>
            )}
          </div>
        ))}
        <div className="min-w-[13.75rem] h-full! flex justify-center items-center border border-dashed border-gray-600 min-h-[7.25rem] rounded">
          <Button
            onClick={onAddCustomStandard}
            theme="secondary-gray"
            size="sm"
            name="Add custom standard"
          />
        </div>
      </div>
      {selectedConfig && selectedConfig.mode !== 'auto' && (
        <div className="flex justify-end mt-6">
          {(() => {
            const existing = new Set(Object.keys(selectedConfig.thresholds));
            const missing = parameters.filter((p) => !existing.has(p.parameterName));
            const options = missing.map((p) => ({
              id: p.parameterName,
              name: p.shortName || p.displayName || p.parameterName
            }));
            return (
              <DropdownControl
                controlPlaceholder={options.length ? 'Add parameter' : 'All parameters added'}
                options={options}
                onSelect={(opt) => handleAddParameter(opt.id)}
                theme="bg-light"
                disabled={options.length === 0}
                position="right"
              />
            );
          })()}
        </div>
      )}
      <div className="flex flex-1 flex-row gap-6 flex-wrap justify-between mt-6">
        {selectedConfig?.thresholds &&
          Object.entries(selectedConfig?.thresholds).map(([key, item]) => {
            return (
              <ThresholdEditor
                onDelete={() => handleThresholdDelete(key as ParametersType)}
                mode={selectedConfig.mode}
                key={key}
                onSave={(values) => onSaveAThreshold(values, key as ParametersType)}
                parameter={key as ParametersType}
                data={item}
                isNew={selectedConfig.isNew}
              />
            );
          })}
      </div>
    </div>
  );
}
